<script>
import Access from '@/utils/access';
import taskListModel from '@/model/taskList';
import projectFileAttachApi from '@/api/projectFileAttach';
import agreementApi from '@/api/agreement';

import CommonSection from '@/components/CommonSection.vue';
import ProjectFileAttachList from '@/components/project/ProjectFileAttachList.vue';
import HelpPopup from '@/components/base/HelpPopup.vue';
import { markVisited } from '@/utils/newPage';
import AgreementStatTiles from '@/components/agreement/AgreementStatTiles.vue';

export default {
  name: 'ProjectFileAttachListPage',
  components: {AgreementStatTiles, CommonSection, ProjectFileAttachList, HelpPopup },
  data() {
    return {
      attaches: [],
      agreements: [],
    };
  },
  computed: {
    currUser() { return this.$store.state.auth.user; },
    access() { return new Access(this.project, this.currUser); },
    project() { return this.$store.state.project.project },
    tasks() { return this.$store.getters['taskList/byStages']; },
    stages() { return this.$store.getters['stage/itemsHydrated'].map(s => ({ ...s, progress: Math.floor(s.numberOfClosedTasks / s.numberOfTasks * 100) })); },
  },
  watch: {
    project: {
      immediate: true,
      handler(val) {
        if (!this.project) return;
        const access = new Access(val, this.$store.state.auth.user);

        const hasAccess = access.isProjectAdmin() || access.isProjectAuthor();
        if (!hasAccess) {
          this.$store.commit('error/add', { status: 403 });
        }
      },
    },
  },
  created() {
    const { projectId } = this.$route.params;

    this.$store.dispatch('project/fetchProject', { projectId, force: true });

    this.$store.commit('taskList/set', []);
    taskListModel.fetch({ 'stage.project': projectId });

    projectFileAttachApi.fetchList({ 'task.stage.project': projectId }, true)
      .then(r => { this.attaches = r.items; });

    agreementApi.fetchList({ 'comment.task.stage.project': projectId }, true)
      .then(r => { this.agreements = r.items; });

    markVisited('projectFileAttaches');
  },
  methods: {
    async detach(file) {
      const found = this.attaches.find(a => a.projectFile.id === file.id);
      if (!found) return;

      await projectFileAttachApi.delete(found.id, true);
      this.attaches = this.attaches.filter(a => a !== found);
    },
    async copyExtUrl() {
      // const { projectId } = this.$route.params;
      // const { extId, hash } = await projectFileAttachApi.getExtAccessToken(projectId, true);
      // const url = `${document.location.origin}/ext/attaches/${extId}/${hash}`;
      // await navigator.clipboard.writeText(url);
      // this.$push({ text: 'Ссылка скопирована в буфер обмена' });


      const { projectId } = this.$route.params;
      const clipboardItem = new ClipboardItem({
        'text/plain': projectFileAttachApi.getExtAccessToken(projectId, true)
            .then(({ extId, hash }) => `${document.location.origin}/ext/attaches/${extId}/${hash}`)
            .then(text => new Blob([text], { type: 'text/plain' }))
      });

      await navigator.clipboard.write([clipboardItem]);
      this.$push({ text: 'Ссылка скопирована в буфер обмена' });
    },
  },
}
</script>

<template lang="pug">
  CommonSection(title="Кабинет заказчика" :back="{ title: project && project.name, name: 'project-stages', params: { projectId: $route.params.projectId } }")
    template(#title)
      HelpPopup(guide="https://teroom.ru/guides/75")
        template(#default)
          h2 Страница "Кабинет заказчика"
          p Кабинет заказчика — это сводная страница всех ключевых документов проекта, доступ к которой можно предоставить по защищенной ссылке для незарегистрированных в TeRoom заказчиков.
          p Для заказчика дизайн-студии этот функционал превращается в итоговый архив всех файлов проекта по завершении работ. В одном месте собираются чертежи, визуализации, спецификации, договоры, чеки, гарантии, инструкции — всё, что нужно для комфортного пользования объектом и защиты своих интересов.
        template(#details)
          h3 Как добавить файл в «Кабинет заказчика»
          p В ходе работы над задачей:
          ol
            li Наведите курсор на иконку файла в карточке задачи.
            li Кликните на три точки (меню действий) и выберите пункт «Закрепить».
            li Файл автоматически добавится в выделенный список под описанием задачи, а также появится на этой странице.

          h3 Структура и отображение
          p На этой странице файлы сгруппированы по задачам внутри разделов проекта. Вы можете фильтровать содержимое, выбрав для отображения один или несколько разделов — это упрощает навигацию, когда в проекте много этапов работ.
          h3 Скачивание данных
          p Вы можете скачать файлы одним архивом на свой компьютер выборочно (из определенных разделов) или все сразу.
          p Файлы в архиве будут сгруппированы по папкам по разделам и задачам. Так же, как они отображаются на этой странице.
          h3 Права доступа
          ul
            li
              b Автор задачи: {{ ' ' }}
              | Может закреплять файлы только в рамках своей задачи.
            li
              b Автор проекта и Администратор: {{ ' ' }}
              | Обладают полными правами. Только эти роли имеют доступ к общей сводной странице «Кабинет заказчика», управляют настройками публичного доступа и могут скачивать общий архив.
          h3 Публичный доступ (Для заказчика)
          p Вы можете поделиться сводной страницей с заказчиком или любым доверенным лицом, даже если у них нет учетной записи в TeRoom. Для этого необходимо отправить специальную сгенерированную ссылку.
          ul
            li
              b Безопасность:{{ ' ' }}
              | По ссылке доступны только закрепленные файлы. Вся информация по проекту, его задачи, переписка и список участников остаются скрытыми.
            li
              b Срок действия:{{ ' ' }}
              | Ссылка активна на протяжении всего хода работ над проектом. Доступ сохраняется даже после того, как проект отправлен в архив.
            li
              b Ограничение доступа:{{ ' ' }}
              | Доступ к странице и списку файлов будет автоматически ограничен только после полного удаления проекта из архива.

    template(#button)
      Btn(dark xl @click="copyExtUrl")
        | Дать доступ к странице



    ProjectFileAttachList(
      :project="project"
      :stages="stages"
      :tasks="tasks"
      :attaches="attaches"
      @detach="detach"
    )
      template(#agreements)
        AgreementStatTiles(:items="agreements")
</template>

<style scoped lang="scss">

</style>
